import { NumberScreen, NumberSkeleton } from "@commerce/channels/NumberScreen";
import { numberQuery } from "@commerce/channels/queries";
import { numberSearchSchema } from "@commerce/channels/search-params";
import { Page } from "@commerce/components/page";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute, notFound } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/channels/whatsapp/$connectionId",
)({
	validateSearch: numberSearchSchema,
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// Tabs, the test sheet and the Activity filters are client state; the loader warms the number.
	shouldReload: false,
	loader: async ({ context, params }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		let name: string | null = null;
		if (context.isOrganizationAdmin) {
			try {
				const number = await queryClient.ensureQueryData(
					numberQuery(organizationId, params.connectionId),
				);
				name = number.label || number.displayPhone;
			} catch (error) {
				// A number of another retailer reads as missing too.
				if ((error as { code?: string }).code === "NOT_FOUND") {
					throw notFound();
				}
				throw error;
			}
		}
		return {
			organizationId,
			name,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: `${loaderData?.name ?? "WhatsApp number"} - ${config.appName}`,
			},
		],
	}),
	component: NumberPage,
});

function NumberPage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { connectionId } = Route.useParams();
	const { organizationId, dehydratedState } = Route.useLoaderData();
	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="channels" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={<NumberSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
			resetKeys={[connectionId]}
		>
			<NumberScreen
				organizationId={organizationId}
				connectionId={connectionId}
			/>
		</AsyncBoundary>
	);
}
